import type { ReactNode } from 'react';
import { TitleBloc } from '@/components/TextBlock';
import type { FontOptions } from '@/lib/fontStyle';

/**
 * The title/text column that `DoughnutWidget`, `HourlyChartWidget` and `FloorOccupancyWidget`
 * all put to the left of their content, and the rule that decides whether it takes up space.
 *
 * The three legacy widgets each hand-rolled the same two things with different class strings:
 * a left column wrapping `TitleBloc` (plus whatever else) in a `text-center lg:text-left`
 * block, and a ternary collapsing the *right* column to full width when there is nothing in
 * the left one. `TopSpacesWidget` has no split at all and does not use this.
 *
 * The collapse rule itself is `collapsesTitleColumn()` in `@/lib/titleColumn` — separate only
 * because a module exporting both a component and a function breaks Fast Refresh.
 */

export type TitleColumnProps = {
  /** The column's width classes — `lg:w-6/12`, `lg:w-1/2` and `lg:flex-1` in the three widgets. */
  className: string;
  title: string | undefined;
  titleFont: FontOptions | undefined;
  /** Whatever the widget puts under its title: a subtitle, a text block, or both. */
  children?: ReactNode;
};

export function TitleColumn({ className, title, titleFont, children }: TitleColumnProps) {
  return (
    <div className={className}>
      <div className="text-center lg:text-left">
        <TitleBloc text={title} font={titleFont} />
        {children}
      </div>
    </div>
  );
}
